import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, FormHelperText, FormLabel, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { templatizeOpenApiDefinition } from 'config-editor-common';
import {
  JsonMonacoEditor,
  TemplatizeSuggestionAlert,
  ValidationErrorsAlert,
  useTemplatizeToggle,
} from './jsonEditorShell';

// Dots allowed for API Gateway stage variable refs, e.g. ${stageVariables.accountId}
const PLACEHOLDER_NAME_RE = /^[A-Za-z_][A-Za-z0-9_.:-]*$/;

function formDataToEditorText(formData: unknown): string {
  if (typeof formData === 'string') return formData;
  if (!formData) return '';
  return JSON.stringify(formData, null, 2);
}

function extractPlaceholderTokens(input: string) {
  const all = Array.from(input.matchAll(/\$\$?\{([^}]+)\}/g))
    .map(m => m[1])
    .filter(Boolean);
  const valid = all.filter(t => PLACEHOLDER_NAME_RE.test(t));
  const invalid = all.filter(t => !PLACEHOLDER_NAME_RE.test(t));
  return { all, valid, invalid };
}

/** API Gateway resolves ${stageVariables.*} at deploy/runtime; not supplied from Backstage template vars. */
function isApiGatewayStageVariablePlaceholder(name: string): boolean {
  return name === 'stageVariables' || name.startsWith('stageVariables.');
}

export interface PlaceholderAwareOpenApiEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Known placeholders that are available (e.g. derived/constants). */
  knownPlaceholders?: string[];
  /** If true, show errors when hardcoded env-specific values are detected. Defaults to true. */
  enforceTemplatizedDefinition?: boolean;
  title?: string;
  description?: string;
  required?: boolean;
  /** When true, show a hint that the file does not exist yet and will be created on save. */
  isNewFile?: boolean;
  /** Called when validation errors change (e.g. for scaffolder form error schema). */
  onValidationChange?: (errors: string[]) => void;
}

/**
 * JSON editor that validates OpenAPI `${placeholder}` usage and provides
 * templatize suggestions for common AWS hardcoded values.
 * Standalone (non-scaffolder) version for use in config editor and elsewhere.
 */
export const PlaceholderAwareOpenApiEditor = (props: PlaceholderAwareOpenApiEditorProps) => {
  const {
    value: valueProp,
    onChange,
    knownPlaceholders = [],
    enforceTemplatizedDefinition = true,
    title = 'OpenAPI JSON',
    description = '',
    required = false,
    isNewFile = false,
    onValidationChange,
  } = props;

  const [value, setValue] = useState<string>(() => formDataToEditorText(valueProp));
  const [jsonErrors, setJsonErrors] = useState<string[]>([]);
  const lastValueRef = useRef(valueProp);

  useEffect(() => {
    const text = formDataToEditorText(valueProp);
    if (lastValueRef.current !== valueProp) {
      lastValueRef.current = valueProp;
      setValue(text);
      setJsonErrors([]);
    }
  }, [valueProp]);

  const handleEditorChange = useCallback(
    (newValue: string | undefined) => {
      if (newValue === undefined) return;
      setValue(newValue);
      onChange(newValue);
    },
    [onChange],
  );

  const placeholderInfo = useMemo(() => {
    const text = value ?? '';
    if (!text.trim()) {
      return { valid: [] as string[], invalid: [] as string[] };
    }
    const out = extractPlaceholderTokens(text);
    return { valid: Array.from(new Set(out.valid)), invalid: Array.from(new Set(out.invalid)) };
  }, [value]);

  const templatizeInfo = useMemo(() => {
    if (!value.trim()) {
      return templatizeOpenApiDefinition('');
    }
    return templatizeOpenApiDefinition(value);
  }, [value]);

  const hardcodedErrors = useMemo(() => {
    if (!enforceTemplatizedDefinition) return [] as string[];
    if (!value.trim()) return [] as string[];

    const errs: string[] = [];
    const f = templatizeInfo.hardcodedFindings;

    if (f.regions.length > 0) {
      errs.push(
        `Hardcoded AWS region(s) detected: ${f.regions
          .slice(0, 5)
          .join(', ')}. Please templatize to \${AWS::Region}.`,
      );
    }
    if (f.accountIds.length > 0) {
      errs.push(
        `Hardcoded AWS account id(s) detected: ${f.accountIds
          .slice(0, 5)
          .join(', ')}. Please templatize to \${AWS::AccountId}.`,
      );
    }
    if (f.buckets.length > 0) {
      errs.push(
        `Hardcoded bucket name(s) detected: ${f.buckets
          .slice(0, 5)
          .join(', ')}. Please templatize to the appropriate \${...} placeholder.`,
      );
    }
    if (f.domains.length > 0) {
      errs.push(
        `Hardcoded domain name(s) detected: ${f.domains
          .slice(0, 5)
          .join(
            ', ',
          )}. Please templatize to the matching \${...} placeholder (\${api_domain_name} for client-api, \${http_proxy_domain_name} for lms.lexipol.com).`,
      );
    }

    return errs;
  }, [enforceTemplatizedDefinition, templatizeInfo.hardcodedFindings, value]);

  const missingPlaceholders = useMemo(() => {
    if (!value.trim()) return [] as string[];
    const available = new Set<string>([...knownPlaceholders]);
    return placeholderInfo.valid.filter(
      p => !available.has(p) && !isApiGatewayStageVariablePlaceholder(p),
    );
  }, [knownPlaceholders, placeholderInfo.valid, value]);

  /** Template/config placeholders only — exclude API Gateway ${stageVariables.*} from UI summaries. */
  const listedTemplatePlaceholders = useMemo(
    () => placeholderInfo.valid.filter(p => !isApiGatewayStageVariablePlaceholder(p)),
    [placeholderInfo.valid],
  );

  const computedErrors = useMemo(() => {
    const errs: string[] = [];
    if (jsonErrors.length > 0) {
      errs.push(...jsonErrors);
    }
    if (value.trim()) {
      if (hardcodedErrors.length > 0) {
        errs.push(...hardcodedErrors);
      }
      if (placeholderInfo.invalid.length > 0) {
        errs.push(
          `Invalid placeholder name(s): ${placeholderInfo.invalid.join(
            ', ',
          )}. Use only letters/numbers/underscores/colons/dots inside \${...} (e.g. \${AWS::Region}, \${stageVariables.accountId}).`,
        );
      }
      if (missingPlaceholders.length > 0) {
        errs.push(
          `Missing template variable(s): ${missingPlaceholders.join(
            ', ',
          )}. Use an available placeholder or update the template options.`,
        );
      }
    }
    return errs;
  }, [jsonErrors, hardcodedErrors, missingPlaceholders, placeholderInfo.invalid, value]);

  useEffect(() => {
    onValidationChange?.(computedErrors);
  }, [computedErrors, onValidationChange]);

  const hasTemplatizeSuggestions = value.trim().length > 0 && templatizeInfo.outputText !== value;
  const commitValue = useCallback(
    (next: string) => {
      setValue(next);
      onChange(next);
    },
    [onChange],
  );
  const templatize = useTemplatizeToggle(value, templatizeInfo.outputText, commitValue);

  return (
    <Box>
      {isNewFile && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
          api.json not found in this API. Edit below and save to create it.
        </Typography>
      )}
      <FormLabel required={required}>{title}</FormLabel>
      {description && <FormHelperText>{description}</FormHelperText>}

      <TemplatizeSuggestionAlert
        show={value.trim().length > 0 && hasTemplatizeSuggestions}
        replacements={templatizeInfo.replacements}
        onApply={templatize.apply}
        onUndo={templatize.undo}
        canUndo={templatize.canUndo}
      />

      <ValidationErrorsAlert errors={computedErrors} />

      {value.trim() &&
        listedTemplatePlaceholders.length > 0 &&
        missingPlaceholders.length === 0 &&
        computedErrors.length === 0 && (
          <Alert severity="success" style={{ marginBottom: 8 }}>
            Placeholders detected: {listedTemplatePlaceholders.join(', ')} (all satisfied)
          </Alert>
        )}

      <JsonMonacoEditor
        value={value}
        onChange={handleEditorChange}
        onSyntaxErrors={setJsonErrors}
      />
    </Box>
  );
};
